<script setup lang="ts">
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import { useAuthStore } from "~/store/auth/auth.store";
import { useDateChoiceStore } from "~/store/date-choice/date-choice.store";
import { ItemType } from "~/utils/enums/common.enums";
import { DateChoiceActionEnum } from "~/utils/enums/date-choice.enums";
import type { AllowedAction } from "@simbelapp/order-sdk";

/**
 * Actions on a pending registration's details: "Choisir une date" and "Modifier le prix" as
 * secondary, "Voir détails" as primary (AC-SUIVI.9, "Détail inscription" mockup).
 * `allowedActions` says what the row allows; the role gate says what the viewer is allowed to manage.
 */
const props = defineProps<{
  projectUserId: string;
  trainingId: string | null;
  training: string | null;
  /** Carrier of the pending registration: a project (draft), or a session as a fallback. */
  projectId: string | null;
  sessionId: string | null;
  allowedActions: AllowedAction[];
  /** With no upcoming session, there is nothing to choose on the participant's behalf (AC-ADN.9). */
  availableSessionsCount: number;
}>();

const authStore = useAuthStore();
const dateChoiceStore = useDateChoiceStore();

function isAllowed(action: DateChoiceActionEnum): boolean {
  return props.allowedActions.some((allowed) => allowed.name === action && allowed.authorized);
}

/**
 * "Voir détails" leads to the individual draft session, where the admin edits costs and can move the
 * registration to another training (AC-SUIVI.16) — not to the catalog page.
 * New tab: the "À planifier" list stays in place behind it.
 */
const carrier = computed(() =>
  props.projectId
    ? { id: props.projectId, type: ItemType.PROJECT }
    : props.sessionId
      ? { id: props.sessionId, type: ItemType.SESSION }
      : null,
);

function openCarrier() {
  if (!carrier.value) return;
  window.open(`/sessions?id=${carrier.value.id}&type=${carrier.value.type}`, "_blank", "noopener,noreferrer");
}

function chooseDate() {
  dateChoiceStore.openAvailableSessionsModal([
    { projectUserId: props.projectUserId, trainingId: props.trainingId, training: props.training },
  ]);
}
</script>

<template>
  <div class="flex flex-wrap justify-center gap-4 px-10 pb-6">
    <AppButton
      v-if="isAllowed(DateChoiceActionEnum.CHOOSE_SESSION)"
      type="outlined"
      label="Choisir une date"
      icon="create"
      :disabled="!availableSessionsCount"
      @click="chooseDate"
    />
    <AppButton
      v-if="isAllowed(DateChoiceActionEnum.EDIT_PRICE) && authStore.isCompanyOrGroupAdmin"
      type="outlined"
      label="Modifier le prix"
      icon="edit"
      @click="dateChoiceStore.showEditPriceModal = true"
    />
    <AppButton
      v-if="isAllowed(DateChoiceActionEnum.VIEW_DETAILS) && authStore.isCompanyOrGroupAdmin"
      label="Voir détails"
      icon="link"
      :disabled="!carrier"
      @click="openCarrier"
    />
  </div>
</template>
